import {
  Avatar,
  AvatarFallbackText,
  AvatarGroup,
  AvatarImage,
} from "@/components/ui/avatar";
import { Box } from "@/components/ui/box";
import { Button, ButtonIcon, ButtonText } from "@/components/ui/button";
import { HStack } from "@/components/ui/hstack";
import { Pressable } from "@/components/ui/pressable";
import { Spinner } from "@/components/ui/spinner";
import { VStack } from "@/components/ui/vstack";
import { useOrganizationParticipantsControllerFindAll } from "@/generated/api/api";
import {
  OrgTransactionResponseDTODataItemItemsItemParticipant,
  ParticipantDTO,
} from "@/generated/api/models";
import { EmptyState } from "@/screens/common/empty.state";
import { getName } from "@/utils/name";
import { Link } from "expo-router";
import { Plus } from "lucide-react-native";
import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from "react";
import { RestaurantTeamInvitationModal } from "./restaurant.team.modal";
import { RestaurantTeamActionSheet } from "./restaurant.team.action.sheet";
import { useAccessTokenClaims, useSession } from "@/utils/session.ctx";
import { FlatList } from "@/components/ui/flat-list";
import { RestaurantTeamItemMini } from "./restaurant.team.item";
import { Fab, FabIcon, FabLabel } from "@/components/ui/fab";

export type RestaurantTeamListMiniProps = {
  organizationId: string;
  onListChanged?: (team?: ParticipantDTO[]) => void;
};

export type RestaurantTeamListMiniRef = {
  refresh: () => void;
};

const PEOPLE_COUNT = 3;

export const RestaurantTeamListMini = forwardRef<
  RestaurantTeamListMiniRef,
  RestaurantTeamListMiniProps
>((props, ref) => {
  const {
    data: response,
    isLoading,
    refetch,
  } = useOrganizationParticipantsControllerFindAll(props.organizationId);

  useEffect(() => {
    props.onListChanged?.(response);
  }, [response]);

  useImperativeHandle(
    ref,
    () => ({
      refresh() {
        refetch();
      },
    }),
    []
  );

  const claims = useAccessTokenClaims();

  const myRole = useMemo(() => {
    return response?.find((p) => p.userId === claims?.externalClaims?.userId)
      ?.role;
  }, [response]);

  const [sheetIsOpen, setSheetIsOpen] = useState(false);
  const [sheetParticipant, setSheetParticipant] = useState<ParticipantDTO>();

  const [invitationOpen, setInvitationOpen] = useState(false);

  return (
    <>
      <Box className="grow gap-4 w-full">
        {isLoading ? (
          <VStack className="w-36 h-full justify-center p-3">
            <Spinner size="large" />
          </VStack>
        ) : !response?.length ? (
          <EmptyState title={"No teammates"} variant="ghost" />
        ) : (
          <HStack className="w-full justify-between items-center">
            <AvatarGroup className="justify-end pl-2">
              {response?.slice(0, PEOPLE_COUNT + 1).map((t, index) => {
                if (index >= PEOPLE_COUNT) {
                  return (
                    <Link
                      href={{
                        pathname: "/(tabs)/orgs/[orgId]/team",
                        params: { orgId: props.organizationId },
                      }}
                    >
                      <Avatar size="lg">
                        <AvatarFallbackText>
                          {"+ " + (response.length - PEOPLE_COUNT) + ""}
                        </AvatarFallbackText>
                      </Avatar>
                    </Link>
                  );
                }

                return (
                  <Pressable
                    key={t.id}
                    onPress={() => {
                      setSheetParticipant(t);
                      setSheetIsOpen(true);
                    }}
                  >
                    <Avatar
                      key={t.id}
                      size="lg"
                      className="border-2 border-outline-0"
                    >
                      <AvatarFallbackText>{getName(t.user)}</AvatarFallbackText>
                      <AvatarImage
                        source={{ uri: t.user.avatarImage ?? undefined }}
                      />
                    </Avatar>
                  </Pressable>
                );
              })}
            </AvatarGroup>
            <Button onPress={() => setInvitationOpen(true)}>
              <ButtonIcon as={Plus} />
              <ButtonText>Invite</ButtonText>
            </Button>
          </HStack>
        )}
      </Box>
      <RestaurantTeamInvitationModal
        organizationId={props.organizationId}
        isOpen={invitationOpen}
        onClose={() => setInvitationOpen(false)}
      />
      <RestaurantTeamActionSheet
        organizationId={props.organizationId}
        isOpen={sheetIsOpen}
        participant={sheetParticipant}
        onClose={() => setSheetIsOpen(false)}
        role={myRole}
      />
    </>
  );
});

export type RestaurantTeamListFullProps = {
  organizationId: string;
};

export function RestaurantTeamListFull(props: RestaurantTeamListFullProps) {
  const {
    data: response,
    isLoadingError,
    isRefetching,
    refetch,
  } = useOrganizationParticipantsControllerFindAll(props.organizationId);

  const claims = useAccessTokenClaims();

  const myRole = useMemo(() => {
    return response?.find((p) => p.userId === claims?.externalClaims?.userId)
      ?.role;
  }, [response]);

  const [sheetIsOpen, setSheetIsOpen] = useState(false);
  const [sheetParticipant, setSheetParticipant] = useState<ParticipantDTO>();

  const [invitationOpen, setInvitationOpen] = useState(false);

  return (
    <>
      <FlatList
        className="w-full h-full"
        contentContainerClassName="grow gap-4 p-4"
        data={response}
        keyExtractor={(info) => info.id}
        refreshing={isRefetching}
        onRefresh={refetch}
        renderItem={(info) => {
          return (
            <Pressable
              className="w-full"
              onPress={() => {
                setSheetParticipant(info.item);
                setSheetIsOpen(true);
              }}
            >
              <RestaurantTeamItemMini participant={info.item} />
            </Pressable>
          );
        }}
        showsHorizontalScrollIndicator={false}
        showsVerticalScrollIndicator={false}
        ListEmptyComponent={() => (
          <VStack className="h-full w-full p-3">
            {response?.length === 0 ? (
              <EmptyState title="No team members" />
            ) : isLoadingError ? (
              <EmptyState
                title="Something went wrong"
                description="Please check your internet connection and try again"
              />
            ) : (
              <Spinner size="large" />
            )}
          </VStack>
        )}
      />
      <Fab
        size="md"
        placement="bottom right"
        isHovered={false}
        isDisabled={false}
        isPressed={false}
        onPress={() => setInvitationOpen(true)}
      >
        <FabIcon as={Plus} />
        <FabLabel>Invite</FabLabel>
      </Fab>
      <RestaurantTeamInvitationModal
        organizationId={props.organizationId}
        isOpen={invitationOpen}
        onClose={() => setInvitationOpen(false)}
      />
      <RestaurantTeamActionSheet
        organizationId={props.organizationId}
        isOpen={sheetIsOpen}
        participant={sheetParticipant}
        onClose={() => setSheetIsOpen(false)}
        role={myRole}
      />
    </>
  );
}
